import { UserFacingError } from '@monetize/types/app';
import { CstNode, CstParser, IToken, Lexer, createToken } from 'chevrotain';
import type jsep from 'jsep';
import { z } from 'zod';
import { FormulaParseError, parseFormula } from './formula/parser';
import { FORMULA_BUILTIN_FUNCTION_NAMES } from './formula/functions';
import { TEMPLATE_PATH_FUNCTION_NAMES } from './formula/template-formula-names';

/**
 * Language parser for template strings contained within user generated string content
 * this is used for things like conditional terms or quote text
 *
 * Examples of valid syntax:
 *
 * {{quote.amount @currency}}
 * {{quote.contractStartDate @date}}
 * {{quote.modifyDate @dateTime}}
 *
 * Use bracket sym
 * {{quote_offering[offeringId="offr_1234"].quote_item[productId="prod_1234"].price}}
 *
 * use @and and @or to indicate how the filter expressions are applied
 * {{quote_offering[offeringId="offr_1234",rateId="rate_1234", @and].quote_item[productId="prod_1234"].price}}
 * {{quote_offering[offeringId="offr_1234",rateId="rate_1234", @or].quote_item[productId="prod_1234"].price}}
 *
 * {{quote_offering[offeringId="offr_1234",@first].quote_item[productId="prod_1234"].price}}
 * {{quote_offering[offeringId="offr_1234",@last].quote_item[productId="prod_1234"].price}}
 *
 * Math formulas are supported via a case-insensitive FORMULA(...) wrapper.
 * The formula body supports numbers/strings/booleans, dot-path field
 * references against the record, + - * / % ** ( ), comparisons, && || !,
 * ternaries, scalar builtins (min, max, abs, floor, ceil, sqrt, pow, ln,
 * log10, round, clamp, coalesce), and path functions taking a single
 * string-literal template path (FIELD, SUM, AVG, COUNT, MIN, MAX).
 * Format directives still apply to the result.
 *
 * {{FORMULA(quote.amount / 12) @currency}}
 * {{FORMULA(round(quote.amount * 1.08, 2)) @number maximumFractionDigits=2}}
 * {{FORMULA(SUM('quote.quoteOfferings[].amount') * 0.9) @currency}}
 * {{FORMULA(FIELD('quote.quoteOfferings[offeringId="offr_1234" @first].amount') * 2)}}
 *
 * Tip: use single-quoted strings inside FORMULA so array-filter values can
 * keep their double quotes without escaping.
 */

/**
 * Output types
 */
export const TemplateExpressionArrayPositionSchema = z.enum([
  'FIRST',
  'LAST',
  'ALL',
]);
export type TemplateExpressionArrayPosition = z.infer<
  typeof TemplateExpressionArrayPositionSchema
>;

export const TemplateExpressionArrayGroupingSchema = z.enum(['AND', 'OR']);
export type TemplateExpressionArrayGrouping = z.infer<
  typeof TemplateExpressionArrayGroupingSchema
>;

export const TemplateExpressionDataFormatSchema = z.enum([
  'CURRENCY',
  'DATE',
  'DATE_TIME',
  'NUMBER',
  'PERCENT',
]);
export type TemplateExpressionDataFormat = z.infer<
  typeof TemplateExpressionDataFormatSchema
>;

export const TemplateExpressionArrayOperatorSchema = z.enum([
  'EQUAL',
  'NOT_EQUAL',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUAL',
  'LESS_THAN',
  'LESS_THAN_OR_EQUAL',
]);
export type TemplateExpressionArrayOperator = z.infer<
  typeof TemplateExpressionArrayOperatorSchema
>;

export const TemplateExpressionFilterBySchema = z.object({
  fieldPath: z.string(),
  operator: TemplateExpressionArrayOperatorSchema,
  value: z.union([z.string(), z.number()]),
});
export type TemplateExpressionFilterBy = z.infer<
  typeof TemplateExpressionFilterBySchema
>;

export const TemplateExpressionFilterSchema = z.object({
  filterBy: z.array(TemplateExpressionFilterBySchema),
  position: TemplateExpressionArrayPositionSchema,
  grouping: TemplateExpressionArrayGroupingSchema,
});
export type TemplateExpressionFilter = z.infer<
  typeof TemplateExpressionFilterSchema
>;

export const TemplateExpressionFieldSchema = z.object({
  fieldPath: z.string(),
  filter: TemplateExpressionFilterSchema.nullable(),
});
export type TemplateExpressionField = z.infer<
  typeof TemplateExpressionFieldSchema
>;

export const TemplateExpressionFormatOptionsSchema = z.object({
  minimumFractionDigits: z.number().nullable(),
  maximumFractionDigits: z.number().nullable(),
});
export type TemplateExpressionFormatOptions = z.infer<
  typeof TemplateExpressionFormatOptionsSchema
>;

export const TemplateExpressionFormulaSchema = z.object({
  source: z.string(),
  ast: z.custom<jsep.Expression>(
    (value) => typeof value === 'object' && value !== null,
  ),
});
export type TemplateExpressionFormula = z.infer<
  typeof TemplateExpressionFormulaSchema
>;

export const TemplateExpressionSchema = z.object({
  fieldPaths: TemplateExpressionFieldSchema.array(),
  formula: TemplateExpressionFormulaSchema.nullable(),
  format: TemplateExpressionDataFormatSchema.nullable(),
  formatOptions: TemplateExpressionFormatOptionsSchema.nullable(),
});
export type TemplateExpression = z.infer<typeof TemplateExpressionSchema>;

/**
 * ***************
 * TOKENS
 * ***************
 */

// Matches "{{"
const DoubleOpenCurly = createToken({
  name: 'DoubleOpenCurly',
  pattern: /\{\{/,
});

// Matches "}}"
const DoubleCloseCurly = createToken({
  name: 'DoubleCloseCurly',
  pattern: /\}\}/,
});

// Matches "."
const Dot = createToken({
  name: 'Dot',
  pattern: /\./,
});

// Matches "["
const LBracket = createToken({
  name: 'LBracket',
  pattern: /\[/,
});

// Matches "]"
const RBracket = createToken({
  name: 'RBracket',
  pattern: /\]/,
});

// Matches ","
const Comma = createToken({
  name: 'Comma',
  pattern: /,/,
});

// Matches "="
const Equal = createToken({
  name: 'Equal',
  pattern: /=/,
});

const NotEqual = createToken({
  name: 'NotEqual',
  pattern: /!=/,
});

const GreaterThanOrEqual = createToken({
  name: 'GreaterThanOrEqual',
  pattern: />=/,
});

const LessThanOrEqual = createToken({
  name: 'LessThanOrEqual',
  pattern: /<=/,
});

const GreaterThan = createToken({
  name: 'GreaterThan',
  pattern: />/,
  longer_alt: GreaterThanOrEqual,
});

const LessThan = createToken({
  name: 'LessThan',
  pattern: /</,
  longer_alt: LessThanOrEqual,
});

// Matches "@" (for @first, @last, etc.)
const AtFirstSymbol = createToken({
  name: 'AtFirstSymbol',
  pattern: /@first/i,
});

const AtLastSymbol = createToken({
  name: 'AtLastSymbol',
  pattern: /@last/i,
});

/**
 * Special operator which can only be used if there are nested arrays
 * e.g. operator against all quote items across all quote offerings
 */
const AtAllSymbol = createToken({
  name: 'AtAllSymbol',
  pattern: /@all/i,
});

const AtAndSymbol = createToken({
  name: 'AtAndSymbol',
  pattern: /@and/i,
});

const AtOrSymbol = createToken({
  name: 'AtOrSymbol',
  pattern: /@or/i,
});

const AtCurrencySymbol = createToken({
  name: 'AtCurrencySymbol',
  pattern: /@currency/i,
});

const AtDateSymbol = createToken({
  name: 'AtDateSymbol',
  pattern: /@date/i,
});

const AtDateTimeSymbol = createToken({
  name: 'AtDateTimeSymbol',
  pattern: /@dateTime/i,
});

const AtNumberSymbol = createToken({
  name: 'AtNumberSymbol',
  pattern: /@number/i,
});

const AtPercentSymbol = createToken({
  name: 'AtPercentSymbol',
  pattern: /@percent/i,
});

const MinimumFractionDigits = createToken({
  name: 'MinimumFractionDigits',
  pattern: /minimumFractionDigits/i,
});

const MaximumFractionDigits = createToken({
  name: 'MaximumFractionDigits',
  pattern: /maximumFractionDigits/i,
});

// Matches identifiers like quote, quote_offering, offeringId, productId, etc.
// e.g. quote_offering, productId, etc.
const Identifier = createToken({
  name: 'Identifier',
  pattern: /[a-zA-Z0-9-_]\w*/,
});

export const SignedDecimal = createToken({
  name: 'SIGNED_DECIMAL',
  pattern: /(-|\+)[0-9]*\.\d+/,
});
export const UnsignedDecimal = createToken({
  name: 'UNSIGNED_DECIMAL',
  pattern: /[0-9]*\.\d+/,
});
export const SignedInteger = createToken({
  name: 'SIGNED_INTEGER',
  pattern: /(-|\+)[0-9]+/,
});
export const UnsignedInteger = createToken({
  name: 'UNSIGNED_INTEGER',
  pattern: /0|[1-9]\d*/,
});

// Matches string literals like "offr_1234"
const StringLiteral = createToken({
  name: 'StringLiteral',
  pattern: /"[^"]*"/,
});

// Whitespace or other “throwaway” tokens
const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /\s+/,
  group: Lexer.SKIPPED,
});

const FORMULA_START_REGEX = /FORMULA\s*\(/iy;

/**
 * Custom chevrotain pattern matching `FORMULA( ... )` with a balanced closing
 * paren. The scan is string-aware: parens inside single- or double-quoted
 * strings (with backslash escapes, matching jsep's string rules) are ignored.
 * The inner formula source is attached as the token payload so the visitor
 * can hand it to the formula parser without re-extraction.
 *
 * Returns null on an unbalanced input so lexing falls through to `Identifier`
 * and the expression fails to parse — the same fail-closed behavior invalid
 * expressions have today.
 */
function matchFormulaLiteral(
  text: string,
  startOffset: number,
): ([string] & { payload?: string }) | null {
  FORMULA_START_REGEX.lastIndex = startOffset;
  const startMatch = FORMULA_START_REGEX.exec(text);
  if (startMatch === null) {
    return null;
  }
  const innerStart = startOffset + startMatch[0].length;
  let parenDepth = 1;
  let quoteChar: string | null = null;
  for (let index = innerStart; index < text.length; index++) {
    const char = text[index];
    if (quoteChar !== null) {
      if (char === '\\') {
        index++;
      } else if (char === quoteChar) {
        quoteChar = null;
      }
    } else if (char === '"' || char === "'") {
      quoteChar = char;
    } else if (char === '(') {
      parenDepth++;
    } else if (char === ')') {
      parenDepth--;
      if (parenDepth === 0) {
        const result: [string] & { payload?: string } = [
          text.slice(startOffset, index + 1),
        ];
        result.payload = text.slice(innerStart, index);
        return result;
      }
    }
  }
  return null;
}

// Matches the entire `FORMULA(...)` call, capturing the formula body as the
// token payload. Must be listed before Identifier; a field simply named
// "formula" (no following paren) still lexes as an Identifier.
const FormulaLiteral = createToken({
  name: 'FormulaLiteral',
  pattern: matchFormulaLiteral,
  line_breaks: true,
  start_chars_hint: ['F', 'f'],
});

/**
 * Group tokens into an array in the correct order
 * Order matters: longer inputs should be checked first if needed
 */
const allTokens = [
  WhiteSpace,
  DoubleOpenCurly,
  DoubleCloseCurly,
  LBracket,
  RBracket,
  Dot,
  Comma,
  Equal,
  NotEqual,
  GreaterThanOrEqual,
  LessThanOrEqual,
  GreaterThan,
  LessThan,
  StringLiteral,
  SignedDecimal,
  UnsignedDecimal,
  SignedInteger,
  UnsignedInteger,
  AtFirstSymbol,
  AtLastSymbol,
  AtAllSymbol,
  AtAndSymbol,
  AtOrSymbol,
  AtCurrencySymbol,
  AtDateTimeSymbol,
  AtDateSymbol,
  AtNumberSymbol,
  AtPercentSymbol,
  MinimumFractionDigits,
  MaximumFractionDigits,
  FormulaLiteral,
  Identifier,
];

/**
 * ***************
 * PARSER
 * ***************
 * Parse a list of tokens into a CST (Concrete Syntax Tree)
 * This is then passed to the visitor to walk the tree nad return the desired data structure
 */
class TemplateExpressionParser extends CstParser {
  constructor() {
    super(allTokens, {
      recoveryEnabled: false,
    });
    this.performSelfAnalysis();
  }

  // Entry point
  public templateExpression = this.RULE('templateExpression', () => {
    this.CONSUME(DoubleOpenCurly);
    this.OR([
      { ALT: () => this.CONSUME(FormulaLiteral) },
      {
        ALT: () => this.AT_LEAST_ONE(() => this.SUBRULE(this.fieldExpression)),
      },
    ]);
    this.OPTION(() => this.SUBRULE(this.formatExpression));
    this.CONSUME(DoubleCloseCurly);
  });

  private fieldExpression = this.RULE('fieldExpression', () => {
    this.AT_LEAST_ONE_SEP({
      SEP: Dot,
      DEF: () => {
        this.CONSUME(Identifier, { LABEL: 'fieldPath' });
      },
    });
    this.OPTION(() => this.SUBRULE(this.expressionArrayConfig));
    this.OPTION2(() => this.CONSUME(Dot));
  });

  private expressionArrayConfig = this.RULE('expressionArrayConfig', () => {
    this.CONSUME(LBracket);
    this.MANY_SEP({
      SEP: Comma,
      DEF: () => this.SUBRULE(this.arrayFilterExpression),
    });
    this.MANY({
      DEF: () => {
        this.OR({
          DEF: [
            { ALT: () => this.SUBRULE(this.arrayPositionExpression) },
            { ALT: () => this.SUBRULE(this.arrayAndOrExpression) },
          ],
        });
      },
    });
    this.CONSUME(RBracket);
  });

  private arrayFilterExpression = this.RULE('arrayFilterExpression', () => {
    this.MANY_SEP({
      SEP: Dot,
      DEF: () => this.CONSUME(Identifier, { LABEL: 'fieldPath' }),
    });
    this.OR([
      { ALT: () => this.CONSUME(Equal, { LABEL: 'operator' }) },
      { ALT: () => this.CONSUME(NotEqual, { LABEL: 'operator' }) },
      { ALT: () => this.CONSUME(GreaterThanOrEqual, { LABEL: 'operator' }) },
      { ALT: () => this.CONSUME(LessThanOrEqual, { LABEL: 'operator' }) },
      { ALT: () => this.CONSUME(GreaterThan, { LABEL: 'operator' }) },
      { ALT: () => this.CONSUME(LessThan, { LABEL: 'operator' }) },
    ]);
    this.OR1([
      {
        ALT: () =>
          this.SUBRULE(this.stringFilterValue, { LABEL: 'filterValue' }),
      },
      {
        ALT: () =>
          this.SUBRULE(this.numberFilterValue, { LABEL: 'filterValue' }),
      },
    ]);
  });

  private stringFilterValue = this.RULE('stringFilterValue', () => {
    this.CONSUME(StringLiteral, { LABEL: 'value' });
  });

  private numberFilterValue = this.RULE('numberFilterValue', () => {
    this.OR([
      { ALT: () => this.CONSUME(SignedDecimal, { LABEL: 'value' }) },
      { ALT: () => this.CONSUME(UnsignedDecimal, { LABEL: 'value' }) },
      { ALT: () => this.CONSUME(SignedInteger, { LABEL: 'value' }) },
      { ALT: () => this.CONSUME(UnsignedInteger, { LABEL: 'value' }) },
    ]);
  });

  private arrayPositionExpression = this.RULE('arrayPositionExpression', () => {
    this.OR([
      { ALT: () => this.CONSUME(AtFirstSymbol) },
      { ALT: () => this.CONSUME(AtLastSymbol) },
      { ALT: () => this.CONSUME(AtAllSymbol) },
    ]);
  });

  private arrayAndOrExpression = this.RULE('arrayAndOrExpression', () => {
    this.OR([
      { ALT: () => this.CONSUME(AtAndSymbol) },
      { ALT: () => this.CONSUME(AtOrSymbol) },
    ]);
  });

  private formatExpression = this.RULE('formatExpression', () => {
    this.OR([
      {
        ALT: () => {
          this.CONSUME(AtCurrencySymbol);
          this.MANY(() => this.SUBRULE(this.formatOptionExpression));
        },
      },
      { ALT: () => this.CONSUME(AtDateSymbol) },
      { ALT: () => this.CONSUME(AtDateTimeSymbol) },
      {
        ALT: () => {
          this.CONSUME(AtNumberSymbol);
          this.MANY2(() => this.SUBRULE2(this.formatOptionExpression));
        },
      },
      {
        ALT: () => {
          this.CONSUME(AtPercentSymbol);
          this.MANY3(() => this.SUBRULE3(this.formatOptionExpression));
        },
      },
    ]);
  });

  private formatOptionExpression = this.RULE('formatOptionExpression', () => {
    this.OR([
      {
        ALT: () => this.CONSUME(MinimumFractionDigits, { LABEL: 'optionName' }),
      },
      {
        ALT: () => this.CONSUME(MaximumFractionDigits, { LABEL: 'optionName' }),
      },
    ]);
    this.CONSUME(Equal);
    this.OR1([
      { ALT: () => this.CONSUME(UnsignedInteger, { LABEL: 'optionValue' }) },
    ]);
  });
}

const parser = new TemplateExpressionParser();
const lexer = new Lexer(allTokens);

/**
 * ***************
 * INTERPRETER
 * ***************
 * This visitor is used to interpret the CST and create the output Array<TemplateExpressionField>
 */
class ExpressionInterpreter extends parser.getBaseCstVisitorConstructor() {
  constructor() {
    super();
    this.validateVisitor();
  }

  templateExpression(ctx: {
    DoubleOpenCurly: IToken[];
    DoubleCloseCurly: IToken[];
    FormulaLiteral?: IToken[];
    fieldExpression: CstNode[];
    formatExpression?: CstNode[];
  }): TemplateExpression {
    const formatResult = ctx.formatExpression
      ? (this.visit(ctx.formatExpression[0]) as {
          format: TemplateExpressionDataFormat | null;
          formatOptions: TemplateExpressionFormatOptions | null;
        })
      : { format: null, formatOptions: null };
    if (ctx.FormulaLiteral) {
      const source = String(ctx.FormulaLiteral[0].payload ?? '');
      let ast: jsep.Expression;
      try {
        ast = parseFormula(source, TEMPLATE_FORMULA_PARSE_OPTIONS);
      } catch (ex) {
        if (ex instanceof FormulaParseError) {
          throw new UserFacingError(
            `Invalid formula: ${ex.message}`,
            'INVALID_EXPRESSION',
            [ex],
          );
        }
        throw ex;
      }
      return {
        fieldPaths: [],
        formula: { source, ast },
        format: formatResult.format,
        formatOptions: formatResult.formatOptions,
      };
    }
    return {
      fieldPaths: ctx.fieldExpression.map((item) => this.visit(item)),
      formula: null,
      format: formatResult.format,
      formatOptions: formatResult.formatOptions,
    };
  }

  fieldExpression(ctx: {
    fieldPath: IToken[];
    Dot: IToken[];
    expressionArrayConfig?: CstNode[];
  }) {
    const fieldPath = ctx.fieldPath.map((item) => item.image).join('.');
    const filter = ctx.expressionArrayConfig
      ? this.visit(ctx.expressionArrayConfig[0])
      : null;
    return {
      fieldPath,
      filter,
    };
  }

  expressionArrayConfig(ctx: {
    LBracket: IToken[];
    RBracket: IToken[];
    arrayFilterExpression?: CstNode[];
    arrayPositionExpression?: CstNode[];
    arrayAndOrExpression?: CstNode[];
  }): TemplateExpressionFilter {
    const filterBy = (ctx.arrayFilterExpression || []).map((item) =>
      this.visit(item),
    );
    // If no filters are explicitly applied and no position is explicitly applied, then default to ALL
    const defaultPosition = filterBy.length === 0 ? 'ALL' : 'FIRST';
    const position = ctx.arrayPositionExpression
      ? this.visit(ctx.arrayPositionExpression[0], {
          default: defaultPosition,
        })
      : defaultPosition;
    const grouping = ctx.arrayAndOrExpression
      ? this.visit(ctx.arrayAndOrExpression[0])
      : 'AND';
    return {
      filterBy,
      position,
      grouping,
    };
  }

  arrayFilterExpression(ctx: {
    fieldPath: IToken[];
    operator: IToken[];
    filterValue: CstNode[];
  }): TemplateExpressionFilterBy {
    const fieldPath = ctx.fieldPath.map((item) => item.image).join('.');
    const operator = getOperator(ctx.operator[0].image);

    const value = this.visit(ctx.filterValue[0]) as string | number;
    return { fieldPath, operator, value };
  }

  stringFilterValue(ctx: { value: IToken[] }) {
    return ctx.value[0].image.replaceAll(/(^")|("$)/g, '').trim();
  }

  numberFilterValue(ctx: { value: IToken[] }) {
    return Number(ctx.value[0].image);
  }

  arrayPositionExpression(
    ctx: {
      AtFirstSymbol?: IToken[];
      AtLastSymbol?: IToken[];
      AtAllSymbol?: IToken[];
    },
    {
      defaultPosition,
    }: { defaultPosition: TemplateExpressionArrayPosition } = {
      defaultPosition: 'ALL',
    },
  ): TemplateExpressionArrayPosition {
    if (Array.isArray(ctx.AtFirstSymbol)) {
      return 'FIRST';
    }
    if (Array.isArray(ctx.AtLastSymbol)) {
      return 'LAST';
    }
    if (Array.isArray(ctx.AtAllSymbol)) {
      return 'ALL';
    }
    return defaultPosition;
  }

  arrayAndOrExpression(ctx: {
    AtAndSymbol?: IToken[];
    AtOrSymbol?: IToken[];
  }): TemplateExpressionArrayGrouping {
    if (Array.isArray(ctx.AtOrSymbol)) {
      return 'OR';
    }
    return 'AND';
  }

  formatExpression(ctx: {
    AtCurrencySymbol?: IToken[];
    AtDateSymbol?: IToken[];
    AtDateTimeSymbol?: IToken[];
    AtNumberSymbol?: IToken[];
    AtPercentSymbol?: IToken[];
    formatOptionExpression?: CstNode[];
  }): {
    format: TemplateExpressionDataFormat | null;
    formatOptions: TemplateExpressionFormatOptions | null;
  } {
    let format: TemplateExpressionDataFormat | null = null;
    if (Array.isArray(ctx.AtCurrencySymbol)) {
      format = 'CURRENCY';
    } else if (Array.isArray(ctx.AtDateSymbol)) {
      format = 'DATE';
    } else if (Array.isArray(ctx.AtDateTimeSymbol)) {
      format = 'DATE_TIME';
    } else if (Array.isArray(ctx.AtNumberSymbol)) {
      format = 'NUMBER';
    } else if (Array.isArray(ctx.AtPercentSymbol)) {
      format = 'PERCENT';
    }

    let formatOptions: TemplateExpressionFormatOptions | null = null;
    if (ctx.formatOptionExpression && ctx.formatOptionExpression.length > 0) {
      formatOptions = {
        minimumFractionDigits: null,
        maximumFractionDigits: null,
      };
      for (const optionNode of ctx.formatOptionExpression) {
        const option = this.visit(optionNode) as {
          name: string;
          value: number;
        };
        if (option.name.toLowerCase() === 'minimumfractiondigits') {
          formatOptions.minimumFractionDigits = option.value;
        } else if (option.name.toLowerCase() === 'maximumfractiondigits') {
          formatOptions.maximumFractionDigits = option.value;
        }
      }
    }

    return { format, formatOptions };
  }

  formatOptionExpression(ctx: {
    optionName: IToken[];
    optionValue: IToken[];
  }): { name: string; value: number } {
    return {
      name: ctx.optionName[0].image,
      value: Number(ctx.optionValue[0].image),
    };
  }
}

function getOperator(value: string): TemplateExpressionArrayOperator {
  switch (value) {
    case '=':
      return 'EQUAL';
    case '!=':
      return 'NOT_EQUAL';
    case '>':
      return 'GREATER_THAN';
    case '>=':
      return 'GREATER_THAN_OR_EQUAL';
    case '<':
      return 'LESS_THAN';
    case '<=':
      return 'LESS_THAN_OR_EQUAL';
    default:
      throw new Error(`Invalid operator: ${value}`);
  }
}

const interpreter = new ExpressionInterpreter();

/**
 * Parse-time options for FORMULA(...) bodies: only known function names are
 * allowed, and path functions (FIELD, SUM, ...) must receive a single string
 * literal that itself parses as a plain template field path.
 */
const TEMPLATE_FORMULA_PARSE_OPTIONS = {
  allowedFunctions: new Set([
    ...FORMULA_BUILTIN_FUNCTION_NAMES,
    ...TEMPLATE_PATH_FUNCTION_NAMES,
  ]),
  staticStringArgFunctions: TEMPLATE_PATH_FUNCTION_NAMES,
  validateStaticStringArg: (_functionName: string, argValue: string) => {
    parseTemplateFieldPath(argValue);
  },
};

/**
 * Parsing a field path runs the full lexer, parser, CST visitor and zod
 * validation. Formula field paths are static string literals (enforced at
 * parse time by `validateStaticStringArg`), so the same handful of paths are
 * otherwise re-parsed on every evaluation of every term. Results are
 * immutable, so they are safe to share between callers.
 *
 * Bounded because the paths are not only the *saved* template corpus: the
 * terms preview resolves unsaved text while an author types, so each
 * intermediate path (`quote.a`, `quote.am`, ...) parses fine and would
 * otherwise be retained for the lifetime of a long-running server process.
 * Least-recently-used entries are evicted so that transient typing does not
 * push the genuinely hot paths out.
 */
const TEMPLATE_FIELD_PATH_CACHE_MAX_SIZE = 500;
const templateFieldPathCache = new Map<string, TemplateExpressionField[]>();

/**
 * Parse a bare template field path (no surrounding curly braces), e.g.
 * `quote.quoteOfferings[offeringId="offr_1" @first].amount`, as used by the
 * FIELD/aggregate functions inside FORMULA(...). Throws UserFacingError if
 * the input is not a pure field path (contains a formula or format
 * directive, or is empty).
 */
export function parseTemplateFieldPath(
  path: string,
): TemplateExpressionField[] {
  const cached = templateFieldPathCache.get(path);
  if (cached) {
    // Re-insert so this key becomes the most recently used one
    templateFieldPathCache.delete(path);
    templateFieldPathCache.set(path, cached);
    return cached;
  }
  const parsed = parseTemplateExpression(`{{${path}}}`);
  if (parsed.formula || parsed.format) {
    throw new UserFacingError(
      'A field path must not contain a formula or format directive',
      'INVALID_EXPRESSION',
    );
  }
  if (parsed.fieldPaths.length === 0) {
    throw new UserFacingError(
      'A field path must reference at least one field',
      'INVALID_EXPRESSION',
    );
  }
  if (templateFieldPathCache.size >= TEMPLATE_FIELD_PATH_CACHE_MAX_SIZE) {
    // Map iterates in insertion order, so the first key is the oldest use
    const oldestPath = templateFieldPathCache.keys().next().value;
    if (oldestPath !== undefined) {
      templateFieldPathCache.delete(oldestPath);
    }
  }
  templateFieldPathCache.set(path, parsed.fieldPaths);
  return parsed.fieldPaths;
}

export function parseTemplateExpression(inputText: string) {
  const lexingResult = lexer.tokenize(inputText);

  if (lexingResult.errors.length > 0) {
    throw new UserFacingError(
      `Lexing errors detected: ${lexingResult.errors?.[0].message}`,
      'INVALID_EXPRESSION',
      lexingResult.errors,
    );
  }

  parser.input = lexingResult.tokens;
  const parseResults = parser.templateExpression();

  if (parser.errors.length > 0) {
    throw new UserFacingError(
      `Parsing errors detected: ${parser.errors?.[0].message}`,
      'INVALID_EXPRESSION',
      parser.errors,
    );
  }

  const results = interpreter.visit(parseResults);

  return TemplateExpressionSchema.parse(results);
}
